import {
  AppstoreOutlined,
  DashboardOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PictureOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  TagOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { SafeUser } from '@shop-monorepo/types';
import { Button, Menu, Tooltip } from 'antd';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { ROUTES } from '@/routes/routes';

interface NavigationProps {
  collapsed: boolean;
  onToggle: () => void;
  user: SafeUser | null;
  onLogout: () => void;
}

const NavWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const MenuWrapper = styled.div`
  flex: 1;

  .ant-menu {
    padding-bottom: 0;
  }
`;

const SidebarFooter = styled.div`
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    padding-bottom: 24px;
  }
`;

const CollapseButton = styled(Button)`
  width: 100%;
  color: #94a3b8;
  border: none;
  background: transparent;
  margin-bottom: 8px;

  &:hover {
    color: #ffffff !important;
    background: rgba(255, 255, 255, 0.1) !important;
  }
`;

const UserRow = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  margin-bottom: 4px;
  overflow: hidden;
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
`;

const UserEmail = styled.span`
  color: #94a3b8;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const LogoutButton = styled(Button)`
  width: 100%;
  color: #94a3b8;
  border: none;
  background: transparent;

  &:hover {
    color: #ff8e8e !important;
    background: rgba(255, 255, 255, 0.05) !important;
  }
`;

function getInitials(email: string | undefined): string {
  if (!email) return '?';
  return email.charAt(0).toUpperCase();
}

export function Navigation({ collapsed, onToggle, user, onLogout }: NavigationProps) {
  const location = useLocation();
  const isOnSettings = location.pathname.startsWith(ROUTES.SETTINGS);

  return (
    <NavWrapper>
      <MenuWrapper>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={isOnSettings ? ['settings-group'] : []}
          items={[
            {
              key: ROUTES.DASHBOARD,
              icon: <DashboardOutlined />,
              label: <Link to={ROUTES.DASHBOARD}>Дашборд</Link>,
            },
            {
              key: ROUTES.PRODUCTS,
              icon: <ShoppingOutlined />,
              label: <Link to={ROUTES.PRODUCTS}>Продукти</Link>,
            },
            {
              key: ROUTES.BRANDS,
              icon: <TagOutlined />,
              label: <Link to={ROUTES.BRANDS}>Бренди</Link>,
            },
            {
              key: ROUTES.CATEGORIES,
              icon: <AppstoreOutlined />,
              label: <Link to={ROUTES.CATEGORIES}>Категорії</Link>,
            },
            {
              key: ROUTES.HERO_SLIDES,
              icon: <PictureOutlined />,
              label: <Link to={ROUTES.HERO_SLIDES}>Слайдер</Link>,
            },
            {
              key: ROUTES.ORDERS,
              icon: <ShoppingCartOutlined />,
              label: <Link to={ROUTES.ORDERS}>Замовлення</Link>,
            },
            {
              key: ROUTES.USERS,
              icon: <TeamOutlined />,
              label: <Link to={ROUTES.USERS}>Користувачі</Link>,
            },
            {
              key: 'settings-group',
              icon: <SettingOutlined />,
              label: 'Налаштування',
              children: [
                {
                  key: ROUTES.SETTINGS_CONTACTS,
                  label: <Link to={ROUTES.SETTINGS_CONTACTS}>Контакти</Link>,
                },
                {
                  key: ROUTES.SETTINGS_SECURITY,
                  label: <Link to={ROUTES.SETTINGS_SECURITY}>Безпека</Link>,
                },
              ],
            },
          ]}
        />
      </MenuWrapper>

      <SidebarFooter>
        <CollapseButton
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onToggle}
        />
        {user && (
          <UserRow $collapsed={collapsed}>
            <Tooltip title={collapsed ? user.email : undefined} placement="right">
              <UserAvatar>{getInitials(user.email)}</UserAvatar>
            </Tooltip>
            {!collapsed && <UserEmail>{user.email}</UserEmail>}
          </UserRow>
        )}
        <LogoutButton type="text" icon={<LogoutOutlined />} onClick={onLogout}>
          {!collapsed && 'Вийти'}
        </LogoutButton>
      </SidebarFooter>
    </NavWrapper>
  );
}
